import {
  RfcAchForm,
  RfcClientPortalAccessForm,
  RfcMecForm,
  RfcSection125Form,
  RfcInternalData,
} from '@common/lib/rfcSchema';
import {Button, type ButtonProps} from '@common-web/components/ui/button';
import {reportError} from '@common-web/lib/errorReporting';
import {FileText} from 'lucide-react';
import {useState} from 'react';
import {pdf, Document, Page, StyleSheet, Text, View} from '@react-pdf/renderer';

type RfcPdfProps = {
  clientName: string;
  section125FormEnabled: boolean;
  mecFormEnabled: boolean;
  section125Form: RfcSection125Form;
  mecForm: RfcMecForm;
  achForm?: RfcAchForm;
  clientPortalAccessForm: RfcClientPortalAccessForm;
  internalData?: RfcInternalData;
  includeInternalFields: boolean;
  showPepm: boolean;
  /**
   * Tenant `rfcAchAuthorization` flag (see resolveRfcAchAuthorizationEnabled).
   * When false, ACH / banking details are omitted from the PDF even if the
   * form data contains them.
   */
  achEnabled: boolean;
};

const C = {
  navy: '#18345a',
  navyDark: '#0f1f33',
  blue: '#4686ba',
  blueLight: '#6ba0cd',
  bluePale: '#EBF4FB',
  grayDark: '#4B5563',
  gray: '#6B7280',
  grayLight: '#9CA3AF',
  grayBorder: '#E5E7EB',
  grayBg: '#F9FAFB',
  white: '#FFFFFF',
  chipBg: '#E5F2FB',
  accentBar: '#4686ba',
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 100,
    paddingBottom: 64,
    paddingHorizontal: 40,
    fontSize: 10,
    backgroundColor: C.white,
  },
  headerBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 6,
    backgroundColor: C.accentBar,
  },
  headerWrap: {
    position: 'absolute',
    top: 6,
    left: 40,
    right: 40,
    paddingTop: 16,
    paddingBottom: 14,
    borderBottomWidth: 2,
    borderBottomColor: C.grayBorder,
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  headerLeft: {flex: 1, paddingRight: 16, minWidth: 0},
  title: {fontSize: 20, fontWeight: 700, color: C.navy, letterSpacing: 0.3},
  clientName: {fontSize: 14, fontWeight: 700, color: C.navy, marginTop: 3},
  subtitle: {fontSize: 10, color: C.gray, marginTop: 2},
  headerRight: {width: 170, flexShrink: 0, alignItems: 'flex-end'},
  headerDate: {fontSize: 8, color: C.grayLight, textAlign: 'right'},
  headerConfidential: {
    fontSize: 7,
    color: C.blue,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginTop: 3,
  },
  section: {
    marginBottom: 14,
  },
  sectionHeader: {
    backgroundColor: C.navy,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  sectionHeaderStandalone: {
    backgroundColor: C.navy,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 4,
    marginBottom: 10,
  },
  sectionTitle: {fontSize: 10, fontWeight: 700, color: C.white, letterSpacing: 0.4},
  sectionBody: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: C.grayBorder,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    backgroundColor: C.white,
  },
  grid: {display: 'flex', flexDirection: 'row', flexWrap: 'wrap'},
  col: {width: '50%', paddingRight: 10, paddingVertical: 5},
  fieldLabel: {
    fontSize: 7.5,
    color: C.grayLight,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  fieldValue: {fontSize: 10, color: C.navyDark, marginTop: 2},
  divider: {height: 1, backgroundColor: C.grayBorder, marginVertical: 10},
  chipView: {
    backgroundColor: C.chipBg,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: '#C8E1F4',
    paddingHorizontal: 8,
    paddingVertical: 3,
    alignSelf: 'flex-start',
    marginTop: 6,
    marginBottom: 6,
  },
  chipText: {fontSize: 7.5, color: C.navy, fontWeight: 700, letterSpacing: 0.3},
  footerWrap: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 48,
    borderTopWidth: 1,
    borderTopColor: C.grayBorder,
    paddingHorizontal: 40,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLeft: {fontSize: 7, color: C.grayLight},
  footerRight: {fontSize: 8, color: C.gray},
  locationCard: {
    marginTop: 8,
    marginBottom: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: C.grayBg,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: C.grayBorder,
  },
  locationName: {fontSize: 11, color: C.navy, fontWeight: 700, marginBottom: 2},
  locationFein: {fontSize: 8, color: C.gray, marginBottom: 6},
  userCard: {
    marginBottom: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: C.grayBg,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: C.grayBorder,
  },
  userCardTitle: {fontSize: 10, color: C.navy, fontWeight: 700, marginBottom: 4},
});

function displayValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function Field({label, value}: {label: string; value: unknown}) {
  return (
    <View style={styles.col}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <Text style={styles.fieldValue}>{displayValue(value)}</Text>
    </View>
  );
}

function AddressBlock({
  label,
  address,
}: {
  label: string;
  address: {street: string; city: string; state: string; zip: string} | undefined;
}) {
  return (
    <View style={{paddingVertical: 3}}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <Text style={styles.fieldValue}>{address?.street || '—'}</Text>
      <Text style={{...styles.fieldValue, fontSize: 9}}>
        {[address?.city, address?.state, address?.zip].filter(Boolean).join(', ') || '—'}
      </Text>
    </View>
  );
}

function Section({
  title,
  children,
  wrap,
}: {
  title: string;
  children: React.ReactNode;
  wrap?: boolean;
}) {
  return (
    <View style={styles.section} wrap={wrap ?? false}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>{title}</Text>
      </View>
      <View style={styles.sectionBody}>{children}</View>
    </View>
  );
}

function SectionHeader({title}: {title: string}) {
  return (
    <View style={styles.sectionHeaderStandalone} wrap={false}>
      <Text style={styles.sectionTitle}>{title}</Text>
    </View>
  );
}

function Chip({children}: {children: React.ReactNode}) {
  return (
    <View style={styles.chipView}>
      <Text style={styles.chipText}>{children as string}</Text>
    </View>
  );
}

function ContactBlock({
  label,
  contact,
}: {
  label: string;
  contact:
    | {firstName?: string; lastName?: string; title?: string; phone?: string; email?: string}
    | undefined;
}) {
  return (
    <View>
      <Chip>{label}</Chip>
      <View style={styles.grid}>
        <Field label='First Name' value={contact?.firstName} />
        <Field label='Last Name' value={contact?.lastName} />
        <Field label='Title' value={contact?.title} />
        <Field label='Phone' value={contact?.phone} />
        <Field label='Email' value={contact?.email} />
      </View>
    </View>
  );
}

function hasAchData(achForm: RfcAchForm | undefined): boolean {
  if (!achForm) return false;
  return !!(achForm.achDetails?.bankName || achForm.achDetails?.routingNumber);
}

function AchPdfBlock({achForm}: {achForm: RfcAchForm}) {
  return (
    <View style={{marginTop: 8}}>
      <Chip>ACH / Banking</Chip>
      <View style={styles.grid}>
        <Field
          label='Business Name on Account'
          value={achForm.bankAccount?.businessNameOnAccount}
        />
        <Field
          label='Account Holder First Name'
          value={achForm.bankAccount?.accountHolderFirstName}
        />
        <Field
          label='Account Holder Last Name'
          value={achForm.bankAccount?.accountHolderLastName}
        />
        <Field label='Contact Phone' value={achForm.bankAccount?.contactPhone} />
        <Field label='Contact Email' value={achForm.bankAccount?.contactEmail} />
      </View>
      <AddressBlock label='Account Address' address={achForm.bankAccount?.address} />
      <View style={styles.divider} />
      <View style={styles.grid}>
        <Field label='Bank Name' value={achForm.achDetails?.bankName} />
        <Field label='Routing Number' value={achForm.achDetails?.routingNumber} />
        <Field label='Account Number' value={achForm.achDetails?.accountNumber} />
      </View>
      <AddressBlock label='Bank Address' address={achForm.achDetails?.bankAddress} />
      <View style={styles.divider} />
      <View style={styles.grid}>
        <Field label='Authorized Signature' value={achForm.authorization?.authorizedSignature} />
        <Field label='Signature Date' value={achForm.authorization?.signatureDate} />
      </View>
    </View>
  );
}

function LocationBlock({
  loc,
  idx,
  achEnabled,
}: {
  loc: RfcSection125Form['groupInfo']['locations'][number];
  idx: number;
  achEnabled: boolean;
}) {
  return (
    <View style={styles.locationCard} wrap={false}>
      <Text style={styles.locationName}>{loc.locationName || `Location ${idx + 1}`}</Text>
      <Text style={styles.locationFein}>FEIN: {loc.fein || '—'}</Text>
      <Text style={styles.locationFein}>
        Payroll Location Code: {loc.payrollLocationCode || '—'}
      </Text>
      <View style={styles.grid}>
        <View style={styles.col}>
          <AddressBlock label='Company Address' address={loc.companyAddress} />
        </View>
        <View style={styles.col}>
          <AddressBlock label='Mailing Address' address={loc.mailingAddress} />
        </View>
      </View>
      <ContactBlock label='Contact' contact={loc.contactPerson} />
      {achEnabled && hasAchData(loc.achForm) && <AchPdfBlock achForm={loc.achForm} />}
    </View>
  );
}

function ProgramSections({
  prefix,
  form,
  showPepm,
  achEnabled,
}: {
  prefix: string;
  form: RfcSection125Form | RfcMecForm;
  showPepm: boolean;
  achEnabled: boolean;
}) {
  const hasLocations = !!form.groupInfo?.locations?.length;
  return (
    <>
      <Section title={`${prefix} — Group Information`} wrap={false}>
        <View style={styles.grid}>
          <Field label='Benefit Year' value={form.groupInfo?.benefitYear} />
          <Field label='Company Legal Name' value={form.groupInfo?.companyName} />
          <Field label='Company DBA' value={form.groupInfo?.companyDba} />
          <Field label='Other Known Name' value={form.groupInfo?.otherKnownName} />
          <Field label='SIC Code' value={form.groupInfo?.sic} />
          <Field
            label='Primary State of Doing Business'
            value={form.groupInfo?.stateOfDoingBusiness}
          />
          <Field label='Total # of Employees' value={form.groupInfo?.totalEmployees} />
          <Field label='Number of Locations' value={form.groupInfo?.numberOfLocations} />
        </View>
      </Section>
      {hasLocations && (
        <View wrap={true} style={{marginBottom: 14}}>
          <View wrap={false}>
            <Chip>Locations</Chip>
            <LocationBlock loc={form.groupInfo!.locations[0]} idx={0} achEnabled={achEnabled} />
          </View>
          {form.groupInfo!.locations.slice(1).map((loc, idx) => (
            <LocationBlock key={idx + 1} loc={loc} idx={idx + 1} achEnabled={achEnabled} />
          ))}
        </View>
      )}

      <Section title={`${prefix} — Eligibility & Administration`} wrap={false}>
        <View style={styles.grid}>
          <Field
            label='Full Time Employees Eligible'
            value={form.admin?.eligibility?.fullTime}
          />
          <Field
            label='Part Time Employees Eligible'
            value={form.admin?.eligibility?.partTime}
          />
          <Field
            label='Waiting Period for Newly Hired Employees (Days)'
            value={form.admin?.waitingPeriod}
          />
          <Field
            label='Payroll Frequencies'
            value={form.admin?.payrollFrequencies
              ?.map((x) => x.replace(/_/g, ' ').toLowerCase())
              ?.join(', ')}
          />
          <Field label='Enrollment Company' value={form.admin?.enrollmentCompany} />
          <Field
            label='Enrollment Type'
            value={form.admin?.enrollmentType?.replace(/_/g, ' ').toLowerCase()}
          />
        </View>
      </Section>

      <Section title={`${prefix} — Contacts`}>
        <ContactBlock label='Authorized Signer (Plan Administrator)' contact={form.contacts?.authorizedSigner} />
        <View style={styles.divider} />
        <ContactBlock label='Eligibility Contact' contact={form.contacts?.eligibilityContact} />
        <View style={styles.divider} />
        <ContactBlock label='Billing Contact' contact={form.contacts?.billingContact} />
      </Section>

      <Section title={`${prefix} — Payroll`}>
        <ContactBlock label='Internal Payroll Contact' contact={form.payrollInfo?.contact} />
        <View style={styles.divider} />
        <ContactBlock label='Payroll Vendor Contact' contact={form.payrollInfo?.payrollVendorContact} />
        <View style={styles.divider} />
        <View style={styles.grid}>
          <Field label='Payroll Vendor' value={form.payrollInfo?.vendor} />
          <Field label='Payroll System' value={form.payrollInfo?.system} />
          <Field label='Payroll Company ID' value={form.payrollInfo?.payrollCompanyId} />
        </View>
      </Section>

      <Section title={`${prefix} — Agent of Record`}>
        <View style={styles.grid}>
          <Field label='First Name' value={form.agentOfRecord?.firstName} />
          <Field label='Last Name' value={form.agentOfRecord?.lastName} />
          <Field label='Phone' value={form.agentOfRecord?.phone} />
          <Field label='Email' value={form.agentOfRecord?.email} />
          <Field label='Additional Phone' value={form.agentOfRecord?.additionalPhone} />
          <Field label='Additional Email' value={form.agentOfRecord?.additionalEmail} />
          {showPepm && <Field label='PEPM' value={form.agentOfRecord?.pepm} />}
        </View>
      </Section>
    </>
  );
}

function formatMecDisplay(mec: string | undefined): string {
  const map: Record<string, string> = {
    CHAMPION_HEALTH: 'Champion Health MEC',
    CHAMPION_HEALTH_HSA: 'Champion Health HSA MEC',
    HEALTHCUES: 'HealthCues MEC',
    QUANTUM: 'Quantum MEC',
  };
  if (!mec) return '—';
  return map[mec] ?? mec.replace(/_/g, ' ');
}

function RfcPdfExport(props: RfcPdfProps) {
  const s125 = props.section125FormEnabled ? props.section125Form : undefined;
  const mec = props.mecFormEnabled ? props.mecForm : undefined;
  const companyName =
    s125?.groupInfo.companyName || mec?.groupInfo.companyName || props.clientName || 'Client';
  const generatedDate = new Date();

  return (
    <Document title={`Request for Coverage – ${companyName}`} author={'CBS Infinity'}>
      <Page size='A4' style={styles.page}>
        {/* Accent bar */}
        <View style={styles.headerBar} fixed />

        {/* Header */}
        <View style={styles.headerWrap} fixed>
          <View style={styles.headerRow}>
            <View style={styles.headerLeft}>
              <Text style={styles.title}>Request for Coverage</Text>
              <Text style={styles.clientName}>{props.clientName}</Text>
              <Text style={styles.subtitle}>{companyName}</Text>
            </View>
            <View style={styles.headerRight}>
              <Text style={styles.headerConfidential}>Confidential</Text>
              <Text style={styles.headerDate}>
                Generated {generatedDate.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})}
              </Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footerWrap} fixed>
          <Text style={styles.footerLeft}>CBS Infinity • Confidential</Text>
          <Text
            style={styles.footerRight}
            render={({pageNumber, totalPages}) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>

        {/* Content */}
        {!!s125 && (
          <ProgramSections
            prefix='125 Program'
            form={s125}
            showPepm={props.showPepm}
            achEnabled={props.achEnabled}
          />
        )}
        {!!mec && (
          <ProgramSections
            prefix='MEC Plan'
            form={mec}
            showPepm={props.showPepm}
            achEnabled={props.achEnabled}
          />
        )}

        {!!props.clientPortalAccessForm?.users?.length && (
          <View wrap={true} style={{marginBottom: 14}}>
            {props.clientPortalAccessForm.users.map((user, idx) => {
              const card = (
                <View key={idx} style={styles.userCard} wrap={false}>
                  <Text style={styles.userCardTitle}>{`User ${idx + 1}`}</Text>
                  <View style={styles.grid}>
                    <Field label='First Name' value={user?.firstName} />
                    <Field label='Last Name' value={user?.lastName} />
                    <Field label='Title' value={user?.title} />
                    <Field label='Phone' value={user?.phone} />
                    <Field label='Email' value={user?.email} />
                  </View>
                </View>
              );
              if (idx === 0) {
                return (
                  <View key='first' wrap={false}>
                    <SectionHeader title='Client Portal Access — Users' />
                    {card}
                  </View>
                );
              }
              return card;
            })}
          </View>
        )}

        {props.includeInternalFields && !!props.internalData && (
          <Section title='Internal Configuration'>
            <View style={styles.grid}>
              <Field label='Group Number' value={props.internalData.groupNumber} />
              <Field
                label='Product Codes'
                value={
                  props.internalData.productCodes && props.internalData.productCodes.length > 0
                    ? props.internalData.productCodes.map((pc) => pc.value).join(', ')
                    : '—'
                }
              />
              <Field label='Carrier' value={props.internalData.carrierName?.replace(/_/g, ' ')} />
              {props.internalData.carrierName === 'OTHER' && (
                <Field label='Carrier (Other)' value={props.internalData.carrierNameOther} />
              )}
              <Field
                label='Telemedicine'
                value={props.internalData.telemedicine?.replace(/_/g, ' ')}
              />
              {props.internalData.telemedicine === 'OTHER' && (
                <Field label='Telemedicine (Other)' value={props.internalData.telemedicineOther} />
              )}
              <Field label='PBM' value={props.internalData.pbm?.replace(/_/g, ' ')} />
              {props.internalData.pbm === 'OTHER' && (
                <Field label='PBM (Other)' value={props.internalData.pbmOther} />
              )}
              <Field label='App' value={props.internalData.app?.replace(/_/g, ' ')} />
              {props.internalData.app === 'OTHER' && (
                <Field label='App (Other)' value={props.internalData.appOther} />
              )}
              <Field label='MEC' value={formatMecDisplay(props.internalData.mec)} />
              {props.internalData.mec === 'OTHER' && (
                <Field label='MEC (Other)' value={props.internalData.mecOther} />
              )}
              <Field
                label='PPO Network'
                value={props.internalData.ppoNetwork?.replace(/_/g, ' ')}
              />
              {props.internalData.ppoNetwork === 'OTHER' && (
                <Field label='PPO Network (Other)' value={props.internalData.ppoNetworkOther} />
              )}
            </View>
          </Section>
        )}
      </Page>
    </Document>
  );
}

type RfcGeneratePdfButtonProps = RfcPdfProps & {
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
};

export function RfcGeneratePdfButton({
  variant = 'outlineDark',
  size,
  className,
  ...props
}: RfcGeneratePdfButtonProps) {
  const [loadingPdf, setLoadingPdf] = useState(false);

  async function handleGeneratePdf() {
    try {
      setLoadingPdf(true);
      const companyName =
        props.section125Form.groupInfo.companyName ||
        props.mecForm.groupInfo.companyName ||
        props.clientName;

      if (!companyName) {
        throw new Error('Company name is required to generate PDF');
      }

      const doc = <RfcPdfExport {...props} />;
      const asPdf = pdf();
      asPdf.updateContainer(doc);
      const blob = await asPdf.toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `request-for-coverage-${companyName.toLowerCase().slice(0, 50).replace(/\s+/g, '-')}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode!.removeChild(link);
    } catch (e) {
      reportError(e, {
        title: 'Could not generate PDF',
        fallbackMessage: 'Failed to generate the PDF. Please try again.',
        context: 'RfcPdfExport.handleGeneratePdf',
      });
    } finally {
      setLoadingPdf(false);
    }
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handleGeneratePdf}
      disabled={loadingPdf}
    >
      <FileText className='h-4 w-4 mr-2' />
      {loadingPdf ? 'Generating…' : 'Generate PDF'}
    </Button>
  );
}
